import React from "react";
import { useCurrentFrame, interpolate, Easing } from "remotion";
import { z } from "zod";

// Animation type enum
export const AnimationType = {
  NONE: "none",
  FADE: "fade",
  SLIDE_TOP: "slideTop",
  SLIDE_BOTTOM: "slideBottom",
  SLIDE_LEFT: "slideLeft",
  SLIDE_RIGHT: "slideRight",
};

// Animation Schema
const AnimationSchema = z.object({
  startFrame: z.number().default(0),
  endFrame: z.number().default(300),
  inAnimation: z
    .enum(["none", "fade", "slideTop", "slideBottom", "slideLeft", "slideRight"])
    .default("fade"),
  inDuration: z.number().default(20),
  outAnimation: z
    .enum(["none", "fade", "slideTop", "slideBottom", "slideLeft", "slideRight"])
    .default("fade"),
  outDuration: z.number().default(15),
});

// Position Schema
const PositionSchema = z.object({
  x: z.number().default(0),
  y: z.number().default(0),
});

// Style Schema
const StyleSchema = z.object({
  opacity: z.number().default(1),
  zIndex: z.number().default(10),
});

// Base animation schema that can be extended
export const BaseOverlaySchema = AnimationSchema.merge(PositionSchema).merge(
  StyleSchema
);

/**
 * Calculate animation values based on animation type and progress
 * @param {string} animationType - Type of animation (fade, slideTop, etc)
 * @param {number} progress - 0 to 1 progress value
 * @param {boolean} isOutAnimation - Whether this is an out animation
 * @returns {object} { opacity, scale, translateX, translateY }
 */
const calculateAnimationValues = (animationType, progress, isOutAnimation = false) => {
  let opacity = 1;
  let scale = 1;
  let translateX = 0;
  let translateY = 0;

  const p = isOutAnimation ? progress : progress;

  switch (animationType) {
    case AnimationType.FADE:
      opacity = isOutAnimation
        ? interpolate(p, [0, 1], [1, 0], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
            easing: Easing.out(Easing.cubic),
          })
        : interpolate(p, [0, 1], [0, 1], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
            easing: Easing.out(Easing.cubic),
          });
      break;

    case AnimationType.SLIDE_TOP:
      opacity = isOutAnimation
        ? interpolate(p, [0, 1], [1, 0], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
          })
        : interpolate(p, [0, 1], [0, 1], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
          });
      translateY = isOutAnimation
        ? interpolate(p, [0, 1], [0, -50], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
            easing: Easing.in(Easing.cubic),
          })
        : interpolate(p, [0, 1], [-50, 0], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
            easing: Easing.out(Easing.cubic),
          });
      break;

    case AnimationType.SLIDE_BOTTOM:
      opacity = isOutAnimation
        ? interpolate(p, [0, 1], [1, 0], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
          })
        : interpolate(p, [0, 1], [0, 1], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
          });
      translateY = isOutAnimation
        ? interpolate(p, [0, 1], [0, 50], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
            easing: Easing.in(Easing.cubic),
          })
        : interpolate(p, [0, 1], [50, 0], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
            easing: Easing.out(Easing.cubic),
          });
      break;

    case AnimationType.SLIDE_LEFT:
      opacity = isOutAnimation
        ? interpolate(p, [0, 1], [1, 0], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
          })
        : interpolate(p, [0, 1], [0, 1], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
          });
      translateX = isOutAnimation
        ? interpolate(p, [0, 1], [0, -50], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
            easing: Easing.in(Easing.cubic),
          })
        : interpolate(p, [0, 1], [50, 0], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
            easing: Easing.out(Easing.cubic),
          });
      break;

    case AnimationType.SLIDE_RIGHT:
      opacity = isOutAnimation
        ? interpolate(p, [0, 1], [1, 0], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
          })
        : interpolate(p, [0, 1], [0, 1], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
          });
      translateX = isOutAnimation
        ? interpolate(p, [0, 1], [0, 50], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
            easing: Easing.in(Easing.cubic),
          })
        : interpolate(p, [0, 1], [-50, 0], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
            easing: Easing.out(Easing.cubic),
          });
      break;

    case AnimationType.NONE:
    default:
      // No animation
      opacity = 1;
      break;
  }

  return { opacity, scale, translateX, translateY };
};

/**
 * Overlay Component
 * - Modular, extendable overlay component
 * - Supports multiple animation types (fade, slide)
 * - Configurable start/end frames and animation durations
 * - Built for reusability across different overlay types
 */
export const Overlay = ({
  startFrame = 0,
  endFrame = 300,
  inAnimation = AnimationType.FADE,
  inDuration = 20,
  outAnimation = AnimationType.FADE,
  outDuration = 15,
  x = 0,
  y = 0,
  opacity: baseOpacity = 1,
  zIndex = 10,
  children = null,
  style = {},
}) => {
  const frame = useCurrentFrame();

  // Don't render if outside the active frame range
  if (frame < startFrame || frame > endFrame) {
    return null;
  }

  const visibleFrames = endFrame - startFrame - inDuration - outDuration;
  const relativeFrame = frame - startFrame;

  let opacity = 1;
  let scale = 1;
  let translateX = 0;
  let translateY = 0;

  // Animation in phase
  if (relativeFrame <= inDuration) {
    const inProgress = relativeFrame / inDuration;
    const inValues = calculateAnimationValues(inAnimation, inProgress, false);
    opacity = inValues.opacity;
    scale = inValues.scale;
    translateX = inValues.translateX;
    translateY = inValues.translateY;
  }
  // Visible phase
  else if (relativeFrame <= inDuration + visibleFrames) {
    opacity = 1;
    scale = 1;
    translateX = 0;
    translateY = 0;
  }
  // Animation out phase
  else {
    const outProgress = (relativeFrame - inDuration - visibleFrames) / outDuration;
    const outValues = calculateAnimationValues(outAnimation, outProgress, true);
    opacity = outValues.opacity;
    scale = outValues.scale;
    translateX = outValues.translateX;
    translateY = outValues.translateY;
  }

  // Apply base opacity
  opacity *= baseOpacity;

  return (
    <div
      style={{
        position: "absolute",
        left: x,
        top: y,
        zIndex,
        opacity,
        transform: `scale(${scale}) translateX(${translateX}px) translateY(${translateY}px)`,
        transformOrigin: "center",
        width: "fit-content",
        height: "fit-content",
        ...style,
      }}
    >
      {children}
    </div>
  );
};

export default Overlay;

;
    var _remotion_globalVariableA, _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
